import { Disclosure, type DisclosureApi } from "@ui/Disclosure";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import {
	type Component,
	createEffect,
	createSignal,
	onCleanup,
	onMount,
	Show,
} from "solid-js";

type Props = {
	text: string;
};

const SMALL_CLASS = "clamped-header-title--long";
const CLAMPED_CLASS = "is-clamped";

const ClampedHeaderTitleControls: Component<{
	text: string;
	api: DisclosureApi;
}> = (props) => {
	const [isSmall, setIsSmall] = createSignal(false);
	const [canExpand, setCanExpand] = createSignal(false);
	let rootRef: HTMLDivElement | undefined;
	let textRef: HTMLSpanElement | undefined;
	let lastWidth = -1;

	/**
	 * Tries the clamped title at large then small size on the live element and
	 * restores its classes before Solid applies the result. Measuring each
	 * size separately (rather than reacting to the current one) is what stops
	 * the large ↔ small flicker, and it all happens before paint.
	 */
	const measure = () => {
		const root = rootRef;
		const el = textRef;
		if (!root || !el || el.clientWidth === 0) {
			return;
		}
		const wasSmall = root.classList.contains(SMALL_CLASS);
		const wasClamped = el.classList.contains(CLAMPED_CLASS);
		const overflowsAt = (small: boolean) => {
			root.classList.toggle(SMALL_CLASS, small);
			return el.scrollHeight > el.clientHeight + 1;
		};

		el.classList.add(CLAMPED_CLASS);
		const needsSmall = overflowsAt(false);
		const truncated = needsSmall && overflowsAt(true);
		root.classList.toggle(SMALL_CLASS, wasSmall);
		el.classList.toggle(CLAMPED_CLASS, wasClamped);

		setIsSmall(needsSmall);
		setCanExpand(truncated);
		if (!truncated && props.api.isOpen()) {
			props.api.toggle();
		}
	};

	onMount(() => {
		const el = textRef;
		if (!el) {
			return;
		}
		measure();
		if (typeof ResizeObserver === "undefined") {
			return;
		}
		// Height changes come from our own font/clamp switches; only width
		// decides which size fits.
		const observer = new ResizeObserver(() => {
			if (el.clientWidth === lastWidth) {
				return;
			}
			lastWidth = el.clientWidth;
			measure();
		});
		observer.observe(el);
		onCleanup(() => observer.disconnect());
	});

	createEffect(() => {
		props.text;
		queueMicrotask(measure);
	});

	return (
		<div
			ref={rootRef}
			class="clamped-header-title"
			classList={{ [SMALL_CLASS]: isSmall() }}
		>
			<span
				ref={textRef}
				class="clamped-header-title__text"
				classList={{ [CLAMPED_CLASS]: !props.api.isOpen() }}
			>
				{props.text}
			</span>
			<Show when={canExpand()}>
				<button
					type="button"
					class="clamped-header-title__toggle gvtui-disclosure-toggle"
					onClick={() => props.api.toggle()}
					aria-expanded={props.api.isOpen()}
					aria-controls={props.api.panelId}
				>
					<span>{props.api.isOpen() ? "Show less" : "Show more"}</span>
					<MaterialSymbol
						symbol="chevron_right"
						class="gvtui-disclosure-icon"
					/>
				</button>
			</Show>
		</div>
	);
};

export const ClampedHeaderTitle = (props: Props) => {
	return (
		<Disclosure
			class="clamped-header-title-disclosure"
			headerIsButton={false}
			renderHeader={(api) => (
				<ClampedHeaderTitleControls text={props.text} api={api} />
			)}
		>
			{/* Panel exists for aria-controls only; expansion is in the header row. */}
			<span
				class="clamped-header-title-disclosure__panel-anchor"
				aria-hidden="true"
			/>
		</Disclosure>
	);
};
